// Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';

// Rxjs
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { map } from 'rxjs/operators/map';
import { tap } from 'rxjs/operators/tap';
import { forkJoin } from 'rxjs/internal/observable/forkJoin';
import { throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import 'rxjs/add/observable/empty';
import 'rxjs/add/operator/retry'; // don't forget the imports
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/of';

// Kendo
import { GridDataResult } from '@progress/kendo-angular-grid';
import { toODataString } from '@progress/kendo-data-query';

// Model
import { ViewCase } from 'app/_models';
import { UniBrandSubReport } from 'app/_models';
import { Task } from 'app/_models';
import { Login } from 'app/_models';
import { UniBrandCalculationModel } from 'app/_models/unibrandcalculationmodel.model';

// Common
import { SharedService } from 'app/_services/shared.service';

// Environment
import { environment } from 'environments/environment';

// Formly
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ToastrService } from 'ngx-toastr';

const httpOptionsJSON = {
    headers: new HttpHeaders({
        'Content-Type': 'application/json; charset=utf-8',
    })
};

const CACHE_SIZE = 1;

@Injectable()
export class WISEService {
    constructor(
        private sharedService: SharedService,
        private httpClient: HttpClient
    ) { }

    public loginUser(loginData: Login): Observable<any> {
        const sUrl = environment.Authentication.LoginURL;

        return this.httpClient
            .post(sUrl, loginData, httpOptionsJSON)
            .retry(0)
            .pipe(
                shareReplay(CACHE_SIZE)
            );
    }

    public syncToWISE(caseID: string): Observable<any> {
        const sUrl = environment.Services.ApiURL + 'wise/sync/' + caseID;

        return this.httpClient
            .post(sUrl, {}, httpOptionsJSON)
            .retry(0)
            .pipe(
                catchError(this.handleError)
            );
    }

    public syncUniversalCalculationToWISE(caseID: string): Observable<any> {
        const sUrl = environment.Services.ApiURL + 'wise/syncuc/' + caseID;

        return this.httpClient
            .post(sUrl, {}, httpOptionsJSON)
            .retry(0)
            .pipe(
                catchError(this.handleError)
            );
    }

    public getUniBrandSubReportList(caseID: string): Observable<UniBrandSubReport> {
        const sUrl = environment.Services.ApiURL + 'unibrand/list/' + caseID;

        return this.httpClient
            .get<UniBrandSubReport>(sUrl, httpOptionsJSON)
            .retry(0)
            .pipe(
                shareReplay(CACHE_SIZE)
            );
    }

    public getUniBrandCalculationModels(caseID: string): Observable<UniBrandCalculationModel> {
        const sUrl = environment.Services.ApiURL + 'unibrand/calculationmodels/' + caseID;

        return this.httpClient
            .get<UniBrandCalculationModel>(sUrl, httpOptionsJSON)
            .retry(0)
            .pipe(
                shareReplay(CACHE_SIZE)
            );
    }

    public getUniBrandSubReportMenuItemList(caseID: string, productionReportCalculationID: string): Observable<any> {
        const sUrl = environment.Services.ApiURL + 'unibrand/menuitems/' + caseID;
        const dataObject = {
            CaseID: caseID,
            ProductionReportCalculationID: productionReportCalculationID
        };

        return this.httpClient
            .post(sUrl, dataObject, httpOptionsJSON)
            .retry(0)
            .pipe(
                shareReplay(CACHE_SIZE)
            );
    }

    public getCaseList(): Observable<ViewCase> {
        const sUrl = environment.Services.ApiURL + 'taskbasket';

        return this.httpClient
            .get<ViewCase>(sUrl, httpOptionsJSON)
            .retry(0)
            .pipe(
                shareReplay(CACHE_SIZE)
            );
    }

    public getVisitList(): Observable<ViewCase> {
        const sUrl = environment.Services.ApiURL + 'visitinfo';

        return this.httpClient
            .get<ViewCase>(sUrl, httpOptionsJSON)
            .retry(0)
            .pipe(
                shareReplay(CACHE_SIZE)
            );
    }

    public getPhotoList(caseID: string): Observable<any> {
        const sUrl = environment.Services.ApiURL + 'photo/list/' + caseID;

        return this.httpClient
            .get(sUrl, httpOptionsJSON)
            .retry(0)
            .pipe(
                shareReplay(CACHE_SIZE)
            )
            .pipe(
                catchError(this.handleError)
            );
    }

    public getInvolvedPersons(caseID: string): Observable<any> {
        const sUrl = `${environment.Services.ApiURL}involvedperson/list/${caseID}`;

        return this.httpClient
            .get(sUrl, httpOptionsJSON)
            .retry(0)
            .pipe(
                shareReplay(CACHE_SIZE)
            )
            .pipe(
                catchError(this.handleError)
            );
    }

    public getInvolvedPersonByPersonType(caseID: string, personType: string = 'VE'): Observable<any> {
        const sUrl = `${environment.Services.ApiURL}involvedperson/${caseID}/${personType}`;

        return this.httpClient
            .get(sUrl, httpOptionsJSON)
            .retry(0)
            .pipe(
                shareReplay(CACHE_SIZE)
            )
            .pipe(
                catchError(this.handleError)
            );
    }

    public getCaseDetailsProperty(caseID: string): Observable<any> {
        const sUrl = environment.Services.ApiURL + 'casedetailsproperty/' + caseID;

        return this.httpClient
            .get(sUrl, httpOptionsJSON)
            .retry(0)
            .pipe(
                shareReplay(CACHE_SIZE)
            )
            .pipe(
                catchError(this.handleError)
            );
    }

    public getCaseReportType(caseID: string): Observable<any> {
        const sUrl = environment.Services.ApiURL + 'casereportingform/' + caseID;

        return this.httpClient
            .get(sUrl, httpOptionsJSON)
            .retry(0)
            .pipe(
                shareReplay(CACHE_SIZE)
            )
            .pipe(
                catchError(this.handleError)
            );
    }

    public getPoliceRegions(caseID: string): Observable<any> {
        const sUrl = environment.Services.ApiURL + 'policeregion/' + caseID;

        return this.httpClient
            .get(sUrl, httpOptionsJSON)
            .retry(0)
            .pipe(
                shareReplay(CACHE_SIZE)
            )
            .pipe(
                catchError(this.handleError)
            );
    }

    public getDocumentList(caseID: string): Observable<Document> {
        const sUrl = environment.Services.ApiURL + 'documentlist/' + caseID;

        return this.httpClient
            .get<Document>(sUrl, httpOptionsJSON)
            .retry(0)
            .pipe(
                shareReplay(CACHE_SIZE)
            )
            .pipe(
                catchError(this.handleError)
            );
    }

    public validateIban(iban: string): Observable<any> {
        const sUrl = `${environment.Services.ApiURL}iban/validate/${iban}`;

        return this.httpClient
            .get(sUrl, httpOptionsJSON)
            .retry(0)
            .pipe(
                catchError(this.handleError)
            );
        // .subscribe(
        // 	(ibanInfo) => {
        // 		if (!ibanInfo.error) {
        // 			this.model.bic = ibanInfo.bic;
        // 			this.model.accountNumber = ibanInfo.accountNumber;
        // 			this.model.bankName = ibanInfo.bankName;
        // 			this.model.countryCode = ibanInfo.countryCode;
        // 			this.ibanStatus = IbanStatus.Valid;
        // 		} else {
        // 			this.ibanStatus = IbanStatus.Invalid;
        // 		}
        // 	},
        // 	(error) => {
        // 		console.log(error);
        // 		error = JSON.parse(error.text());
        // 		this.notifier.error(error.exceptionMessage || 'Server error');
        // 	}
        // );
    }

    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            console.error('Er is een fout opgetreden:', error.error.message);
        } else if (typeof error === 'string' || error instanceof String) {
            console.error(`Backend returned code ${error}`);
        } else {
            console.error(
                `Backend returned code ${error.status}, ` +
                `body was: ${error.error}`);
        }

        return throwError(
            'Er is een fout opgetreden - probeer later opnieuw.');
    }

    public getCaseData(): Observable<any> {
        return forkJoin([this.getCase(), this.getFormFields()]);
    }

    public getCase(): Observable<any> {
        return this.httpClient.get<{ firstName: string, lastName: string }>('assets/case.json');
    }

    public getFormFields(): Observable<any> {
        return this.httpClient
            .get<FormlyFieldConfig[]>('assets/case-form.json')
            .pipe(
                shareReplay(CACHE_SIZE)
            );
    }

}

// export abstract class __WISEService extends BehaviorSubject<GridDataResult> {
//     public loading: boolean;

//     private BASE_URL = 'https://u4iaapi.free.beeceptor.com/';

//     constructor(
//         private http: HttpClient,
//         protected tableName: string
//     ) {
//         super(null);
//     }

//     public query(state: any): void {
//         this.fetch(this.tableName, state)
//             .subscribe(x => super.next(x));
//     }

//     protected fetch(tableName: string, state: any): Observable<GridDataResult> {
//         const queryStr = `${toODataString(state)}&$count=true`;
//         this.loading = true;

//         return this.http
//             .get(`${this.BASE_URL}${tableName}?${queryStr}`)
//             .pipe(
//                 map(response => (<GridDataResult>{
//                     data: response['value'],
//                     total: parseInt(response['@odata.count'], 10)
//                 })),
//                 tap(() => this.loading = false)
//             );
//     }

//     public getFormFields() {
//         return this.http.get<FormlyFieldConfig[]>('assets/json-powered/user-form_json');
//     }
// }

// export class _WISEService {

//     constructor(private http: HttpClient) {
//     }

//     getAllTasks(): Observable<Task[]> {
//         return this.http.get<Task[]>('https://u4iaapi.free.beeceptor.com/tasklist');
//     }
// }

// @Injectable()
// export class _TaskListService extends WISEService {
//     constructor(http: HttpClient) { super(http, 'tasklist'); }

//     public queryForExpertUser({ ExpertUserID }: { ExpertUserID: number }, state?: any): void {
//         this.query(Object.assign({}, state, {
//             filter: {
//                 filters: [{
//                     field: 'ExpertUserID', operator: 'eq', value: ExpertUserID
//                 }],
//                 logic: 'and'
//             }
//         }));
//     }

//     public queryForProductName(ProductName: string, state?: any): void {
//         this.query(Object.assign({}, state, {
//             filter: {
//                 filters: [{
//                     field: 'ProductName', operator: 'contains', value: ProductName
//                 }],
//                 logic: 'and'
//             }
//         }));
//     }

// }

// @Injectable()
// export class TaskListService extends WISEService {
//     constructor(http: HttpClient) { super(http, 'tasklist'); }

//     queryAll(st?: any): Observable<GridDataResult> {
//         const state = Object.assign({}, st);
//         delete state.skip;
//         delete state.take;

//         return this.fetch(this.tableName, state);
//     }
// }
